import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { Utensils, Layout, Save, ChevronRight, Plus, Type, Lock, Store, Award, Sparkles, Instagram, MapPin, Package } from 'lucide-react';
import DigitalMenu from '../../../components/DigitalMenu';
import { useMenuData } from './hooks/useMenuData';
import MenuEditor from './components/MenuEditorComponent';
import ProductsManager from './components/ProductsManager';
import MediaModal from './components/MediaModal';

const MenuManagement = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { tenantData, userData, hasPermission, permissionsLoading, effectiveTenantId } = useAuth();

    const [activeTab, setActiveTab] = useState('products');

    React.useEffect(() => {
        if (location.pathname.endsWith('/editor')) {
            setActiveTab('editor');
        } else if (location.pathname.endsWith('/products')) {
            setActiveTab('products');
        } else {
            setActiveTab('products');
        }
    }, [location.pathname]);

    const handleTabClick = (id) => {
        navigate(`/app/menu/${id}`);
    };

    const [expandedCategoryIdx, setExpandedCategoryIdx] = useState(null);
    const [mediaModal, setMediaModal] = useState({ isOpen: false, target: null });

    const { savedMenu, generatedMenu, setGeneratedMenu, loading, saving, error, handleSaveMenu } = useMenuData(effectiveTenantId, tenantData);

    const activeMenu = generatedMenu || savedMenu;

    if (permissionsLoading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}><div className="animate-spin" style={{ width: '32px', height: '32px', border: '4px solid var(--primary)', borderTopColor: 'transparent', borderRadius: '50%' }}></div></div>;

    if (!userData?.isSuperAdmin && !hasPermission('menu', 'view')) {
        return <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh' }}><Lock size={48} style={{ color: 'var(--text-muted)', marginBottom: '1rem', opacity: 0.5 }} /><h3>{t('common.accessDenied')}</h3></div>;
    }

    const handleMediaApply = (url) => {
        if (!url) return;
        const newMenu = { ...activeMenu };
        if (mediaModal.target.type === 'category') {
            newMenu.categories[mediaModal.target.cIdx].image = url;
        } else {
            newMenu.categories[mediaModal.target.cIdx].items[mediaModal.target.iIdx].image = url;
        }
        setGeneratedMenu(newMenu);
        setMediaModal({ isOpen: false, target: null });
    };

    return (
        <div className="fade-in" style={{ padding: '0 0 2rem 0' }}>
            <header style={{ marginBottom: '2.5rem' }}>
                <h1 style={{ fontSize: '2.25rem', fontWeight: 900, letterSpacing: '-0.02em', marginBottom: '0.5rem' }}>{activeTab === 'products' ? 'Ürünler' : 'Menü Editörü'}</h1>
                <p style={{ color: 'var(--text-muted)', fontWeight: 600 }}>{activeTab === 'products' ? 'İşletmenizin ürünlerini buradan yönetin.' : 'Dijital menünüzü özelleştirin ve düzenleyin.'}</p>
            </header>

            {/* Tabs */}
            <div style={{
                display: 'flex',
                gap: '0.5rem',
                marginBottom: '2.5rem',
                background: 'rgba(var(--primary-rgb), 0.05)',
                padding: '0.5rem',
                borderRadius: '16px',
                width: 'fit-content',
                maxWidth: '100%',
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'none'
            }} className="hide-scrollbar">
                {[
                    ...((activeMenu) ? [{ id: 'products', label: 'Ürünler', icon: Package }] : []),
                    ...((activeMenu) ? [{ id: 'editor', label: 'Menü Editörü', icon: Type }] : [])
                ].map(tab => (
                    <button key={tab.id} onClick={() => handleTabClick(tab.id)} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '12px', border: 'none', background: activeTab === tab.id ? 'var(--primary)' : 'transparent', color: activeTab === tab.id ? 'var(--text-on-primary)' : 'var(--text-main)', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap' }}>
                        <tab.icon size={18} /> {tab.label}
                    </button>
                ))}
            </div>
            <style>{`
                .hide-scrollbar::-webkit-scrollbar { display: none; }
                @media (max-width: 1024px) {
                    .menu-preview-sidebar { display: none !important; }
                }
                @media (max-width: 600px) {
                    .hide-scrollbar button { padding: 8px 12px !important; font-size: 0.85rem !important; }
                    .hide-scrollbar { gap: 0.25rem !important; }
                }
            `}</style>

            {error && <div style={{ padding: '1rem', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', borderRadius: '12px', marginBottom: '1.5rem' }}>{error}</div>}

            {/* Content Area */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '4rem' }}>Yükleniyor...</div>
            ) : (
                <div className="fade-in">
                    {/* Tab 1: Editor */}
                    {activeTab === 'editor' && activeMenu && (
                        <div style={{ display: 'flex', gap: '2.5rem', alignItems: 'flex-start', flexWrap: 'wrap', maxWidth: '1400px', margin: '0 auto' }}>
                            {/* Editor Form */}
                            <div className="card glass" style={{ flex: '1 1 600px', minWidth: 0, padding: '2.5rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                                    <h2 style={{ fontSize: '1.5rem', fontWeight: 800 }}>Menü Editörü</h2>
                                    <div style={{ display: 'flex', gap: '12px' }}>
                                        <button onClick={() => setGeneratedMenu(savedMenu || { menuName: tenantData?.name || 'Menü', description: '', categories: [], designRecommendation: {} })} style={{ padding: '10px 20px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--bg-main)', color: 'var(--text-main)', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}>
                                            Vazgeç
                                        </button>
                                        <button onClick={() => handleSaveMenu(activeMenu)} disabled={saving} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '12px', border: 'none' }}>
                                            <Save size={18} /> {saving ? 'Kaydediliyor...' : t('common.save')}
                                        </button>
                                    </div>
                                </div>
                                <MenuEditor
                                    menu={activeMenu}
                                    onChange={setGeneratedMenu}
                                    onOpenMediaModal={(type, cIdx, iIdx = null) => setMediaModal({ isOpen: true, target: { type, cIdx, iIdx } })}
                                />
                            </div>

                            {/* Mobile Preview Panel */}
                            <div className="menu-preview-sidebar" style={{ width: '380px', flexShrink: 0, position: 'sticky', top: '100px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <div style={{ marginBottom: '1rem', fontWeight: 700, color: 'var(--text-muted)', fontSize: '0.8125rem', letterSpacing: '0.1em', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    MÜŞTERİ ÖNİZLEMESİ
                                </div>
                                {/* Phone Frame */}
                                <div style={{ 
                                    width: '360px', 
                                    height: '740px', 
                                    background: '#111827', 
                                    borderRadius: '40px', 
                                    padding: '12px', 
                                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), inset 0 0 0 2px #374151',
                                    position: 'relative'
                                }}>
                                    {/* Speaker/Camera Notch */}
                                    <div style={{ position: 'absolute', top: '12px', left: '50%', transform: 'translateX(-50%)', width: '120px', height: '28px', background: '#111827', borderBottomLeftRadius: '16px', borderBottomRightRadius: '16px', zIndex: 10, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                        <div style={{ width: '40px', height: '4px', background: '#374151', borderRadius: '4px' }}></div>
                                    </div>
                                    
                                    {/* Screen Content */}
                                    <div style={{ 
                                        width: '100%', 
                                        height: '100%', 
                                        backgroundColor: activeMenu.designRecommendation?.bgColor || '#f5f5f7',
                                        borderRadius: '32px', 
                                        overflowY: 'auto',
                                        overflowX: 'hidden',
                                        position: 'relative',
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }} className="hide-scrollbar">
                                        {/* Standardized Menu Header */}
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem', marginTop: '2.5rem', padding: '0 1rem', textAlign: 'center' }}>
                                            <div style={{ width: '50px', height: '3px', background: activeMenu.designRecommendation?.primaryColor || '#6f4e37', borderRadius: '2px', marginBottom: '1rem', opacity: 0.9 }}></div>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                                                <Utensils size={20} color={activeMenu.designRecommendation?.primaryColor || '#6f4e37'} style={{ opacity: 0.85 }} />
                                                <h3 style={{ fontSize: '1.75rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '4px', color: 'var(--text-main)', margin: 0 }}>
                                                    {activeMenu.menuName || activeMenu.menu_name || 'MENÜ'}
                                                </h3>
                                                <Utensils size={20} color={activeMenu.designRecommendation?.primaryColor || '#6f4e37'} style={{ opacity: 0.85 }} />
                                            </div>
                                            {activeMenu.description && (
                                                <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontStyle: 'italic', marginTop: '0.5rem', maxWidth: '280px' }}>
                                                    {activeMenu.description}
                                                </p>
                                            )}
                                        </div>

                                        {/* Original Digital Menu Render */}
                                        <div style={{ 
                                            flex: 1,
                                            padding: '8px 16px 24px 16px'
                                        }}>
                                            <DigitalMenu menuData={activeMenu} tenant={tenantData} primaryColor={activeMenu.designRecommendation?.primaryColor} hideHeader={true} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Tab: Products */}
                    {activeTab === 'products' && activeMenu && (
                        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
                            <div className="card glass" style={{ padding: '2.5rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '2rem', gap: '12px' }}>
                                    <button onClick={() => setGeneratedMenu(savedMenu)} style={{ padding: '10px 20px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--bg-main)', color: 'var(--text-main)', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}>
                                        Vazgeç
                                    </button>
                                    <button onClick={() => handleSaveMenu(activeMenu)} disabled={saving} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '12px', border: 'none' }}>
                                        <Save size={18} /> {saving ? 'Kaydediliyor...' : t('common.save')}
                                    </button>
                                </div>
                                <ProductsManager
                                    menu={activeMenu}
                                    onChange={setGeneratedMenu}
                                    onOpenMediaModal={(type, cIdx, iIdx = null) => setMediaModal({ isOpen: true, target: { type, cIdx, iIdx } })}
                                />
                            </div>
                        </div>
                    )}

                    {/* Tab 3: My Menus (Default) */}
                    {activeTab === 'myMenus' && (
                        savedMenu ? (
                            <div className="card glass" style={{ padding: '2rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                                    <div>
                                        <h2 style={{ fontSize: '1.5rem', fontWeight: 800 }}>{savedMenu.menuName}</h2>
                                        <p style={{ color: 'var(--text-muted)' }}>{savedMenu.description}</p>
                                    </div>
                                    <button onClick={() => { setGeneratedMenu(savedMenu); setActiveTab('editor'); }} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '12px', border: 'none' }}>
                                        <Plus size={18} /> {t('common.edit')}
                                    </button>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                                    {savedMenu.categories?.map((cat, i) => {
                                        const isExpanded = expandedCategoryIdx === i;
                                        return (
                                            <div key={i} className="card-premium" style={{ padding: 0, overflow: 'hidden', border: '1px solid var(--border)', borderRadius: '16px' }}>
                                                <div onClick={() => setExpandedCategoryIdx(isExpanded ? null : i)} style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', padding: '1.25rem', cursor: 'pointer', background: isExpanded ? 'rgba(var(--primary-rgb), 0.03)' : 'transparent' }}>
                                                    <div style={{ width: '60px', height: '60px', borderRadius: '12px', overflow: 'hidden', flexShrink: 0, background: 'var(--bg-main)' }}>
                                                        {cat.image ? <img src={cat.image} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
                                                    </div>
                                                    <div style={{ flex: 1 }}>
                                                        <h4 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.25rem' }}>{cat.categoryName}</h4>
                                                        <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>{cat.items?.length || 0} {t('nav.items', 'ürün')}</p>
                                                    </div>
                                                    <ChevronRight size={20} style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.3s', color: 'var(--text-muted)' }} />
                                                </div>
                                                {isExpanded && (
                                                    <div style={{ padding: '0 1.5rem 1.5rem 1.5rem', borderTop: '1px solid var(--border)' }}>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1rem' }}>
                                                            {(cat.items || []).map((item, itemIdx) => (
                                                                <div key={itemIdx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: 'var(--bg-main)', borderRadius: '10px', border: '1px solid var(--border)' }}>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                                                        {item.image && <img src={item.image} style={{ width: '32px', height: '32px', borderRadius: '6px', objectFit: 'cover' }} />}
                                                                        <span style={{ fontSize: '0.925rem', fontWeight: 600 }}>{item.name}</span>
                                                                    </div>
                                                                    <span style={{ fontWeight: 800, color: 'var(--primary)', fontSize: '0.875rem' }}>{item.price} ₺</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : (
                            <div style={{ textAlign: 'center', padding: '5rem 2rem', background: 'rgba(var(--primary-rgb), 0.02)', borderRadius: '24px', border: '2px dashed var(--border)' }}>
                                <div style={{ width: '80px', height: '80px', background: 'rgba(var(--primary-rgb), 0.1)', color: 'var(--primary)', borderRadius: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
                                    <Utensils size={40} />
                                </div>
                                <h3 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.5rem' }}>{t('menu.noMenus', 'Henüz menü oluşturulmadı.')}</h3>
                                <p style={{ color: 'var(--text-muted)', marginBottom: '2rem' }}>Lütfen menü oluşturmak için başlayın.</p>
                                <button onClick={() => { setGeneratedMenu({ menuName: 'Yeni Menü', categories: [] }); setActiveTab('editor'); }} className="btn-primary" style={{ padding: '12px 24px', borderRadius: '12px', border: 'none' }}>
                                    {t('common.add')}
                                </button>
                            </div>
                        )
                    )}
                </div>
            )}

            <MediaModal
                isOpen={mediaModal.isOpen}
                onClose={() => setMediaModal({ isOpen: false, target: null })}
                onApply={handleMediaApply}
                effectiveTenantId={effectiveTenantId}
                tenantData={tenantData}
            />
        </div>
    );
};

export default MenuManagement;